import { useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';

/**
 * Organisations come from the DB (admin panel → Organisations tab), not a
 * hardcoded list — an admin can add one at any time and every dropdown has to
 * pick it up without a redeploy.
 */
export type OrganisationOption = {
	key: string;
	fullName: string;
	logoUrl: string | null;
};

/** Absolute URL for an organisation logo (the API returns a relative path). */
export const organisationLogoSrc = (org: { logoUrl?: string | null } | null | undefined) =>
	org?.logoUrl ? `${BACKEND_URL}${org.logoUrl}` : null;

export async function fetchOrganisations(signal?: AbortSignal): Promise<OrganisationOption[]> {
	const res = await fetch(`${BACKEND_URL}/api/organisations`, {
		credentials: 'include',
		signal,
	});
	if (!res.ok) throw new Error(`HTTP ${res.status}`);
	const data = await res.json();
	return Array.isArray(data) ? data : [];
}

/**
 * Shared loader for the org dropdowns in the user/authority admin panels.
 * Failure degrades to an empty list: the selector still offers "None", so a
 * blip can't wedge the whole page.
 */
export function useOrganisations(): OrganisationOption[] {
	const [orgs, setOrgs] = useState<OrganisationOption[]>([]);

	useEffect(() => {
		const ctrl = new AbortController();
		fetchOrganisations(ctrl.signal)
			.then(setOrgs)
			.catch((err) => {
				if (err?.name !== 'AbortError') console.error('Could not load organisations', err);
			});
		return () => ctrl.abort();
	}, []);

	return orgs;
}
